import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon, PrinterIcon, XIcon, AtSymbolIcon, CheckIcon, PlusIcon } from '@heroicons/react/solid';
import { GET_COMPANY_USERS } from 'data/company';
import { useRouter } from 'next/router';
import { useQuery } from 'urql';
import { CompanyUser } from 'components/UsersList/UsersList';
import Avatar from 'components/Avatar';
import { classNames } from 'utils';
import button from 'styles/button';
import {
  ExportFormat,
  ExportMethod,
  ExportTimeRangeOptions,
  ExportTypes,
  ScoreBoardExportSections,
} from 'types/export';
import Loader from 'components/Loader';
import isEmail from 'utils/validateEmail';
import { useReactToPrint } from 'react-to-print';
import React from 'react';
import { useSession } from 'next-auth/react';
import { serverClient } from 'src/urqlClient';
import ScoreboardDataExtractor from 'utils/scoreboardDataExtractor';
import { DateRangePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import 'react-dates/initialize';
import moment from 'moment';
import { ErrorToast } from 'components/Toast';
import { ErrorToastError } from 'components/Toast/ErrorToast';
import { Download } from 'lucide-react';

interface ExportPopupProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  setSuccess: (message: string) => void;
  type?: ExportTypes;
}

const ExportPopup: React.FC<ExportPopupProps> = ({ setIsOpen, isOpen, setSuccess }) => {
  const cancelButtonRef = useRef(null);
  const router = useRouter();

  // const [exportMethod] = useState<ExportMethod>(ExportMethod.Print); // Scoreboard only uses Print method
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [emailRecipients, setEmailRecipients] = useState<CompanyUser[]>([]);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat | undefined>(ExportFormat.CSV);
  const [selectedTimeRange, setSelectedTimeRange] = useState<ExportTimeRangeOptions | undefined>(
    ExportTimeRangeOptions.All,
  );
  const [exportMethod, setExportMethod] = useState<ExportMethod>(ExportMethod.Email);
  const [showTimeRangeError, setShowTimeRangeError] = useState<boolean>(false);
  const [showFormatError, setShowFormatError] = useState<boolean>(false);
  const [showRecipientsError, setShowRecipientsError] = useState<boolean>(false);
  const [showCustomEmailDialog, setShowCustomEmailDialog] = useState<boolean>(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [customEmail, setCustomEmail] = useState<string | undefined>();
  const [emailSent, setEmailSent] = useState(false);
  const [customEmailRecipients, setCustomemailRecipients] = useState<string[]>([]);
  const [showEmailError, setshowEmailerror] = useState<boolean>(false);
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState<moment.Moment | null>(moment());
  const [endDate, setEndDate] = useState<moment.Moment | null>(moment());

  // DateRangePicker focused input state (only for scoreboard)
  const [focusedInput, setFocusedInput] = useState<string>();

  const [selectedScoreboardSections, setSelectedScoreboardSections] = useState<ScoreBoardExportSections[]>([]);
  const [preparingPrint, setPreparingPrint] = useState<boolean>(false);
  const [printInitiated, setPrintInitiated] = useState(false);
  const [error, setError] = useState<ErrorToastError>();
  const { data: session } = useSession();
  const companyId = router.query.company;
  const teamId = router.query.team;

  const [usersResult] = useQuery({
    query: GET_COMPANY_USERS,
    variables: { company_id: companyId },
  });

  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onAfterPrint: () => resetPrintState(),
  });

  const fetchDataAndPrint = async () => {
    setShowTimeRangeError(false);
    setShowFormatError(false);
    setShowRecipientsError(false);

    // Validate selections
    if (!selectedTimeRange) {
      setShowTimeRangeError(true);
    }
    if (!selectedFormat) {
      setShowFormatError(true);
    }

    if (selectedTimeRange && selectedFormat) {
      const client = serverClient(session?.token as unknown as string);
      const extractor = ScoreboardDataExtractor.getInstance(client);
      const customTimeRangeOptions = {
        startDate: startDate?.toDate(),
        endDate: endDate?.toDate(),
      };

      const fetchedData = await extractor.extractDataForMultipleSections(
        Number(companyId),
        Number(teamId),
        ExportTypes.Scoreboard,
        selectedTimeRange,
        customTimeRangeOptions,
        selectedScoreboardSections,
      );

      if (Array.isArray(fetchedData) && fetchedData.length === 0) {
        setError({
          message: 'No records found for the specified duration',
          name: 'No Content',
        });
      } else {
        setData(fetchedData);

        // Handle different export formats
        if (selectedFormat === ExportFormat.CSV) {
          const filename = `scoreboard-export-${new Date().toISOString().split('T')[0]}.csv`;
          downloadCSV(fetchedData, filename);
        } else if (selectedFormat === ExportFormat.PDF) {
          // Handle PDF export (existing print logic)
          setPreparingPrint(true);
        }
      }
    }
  };

  useEffect(() => {
    if (data && data.length > 0 && !printInitiated && selectedFormat === ExportFormat.PDF) {
      handlePrint();
      setPrintInitiated(true); // Ensure print is only initiated once
    } else if (data && data.length > 0 && selectedFormat === ExportFormat.CSV) {
      // Show success message for CSV download
      setSuccess(`Successfully exported ${data.length} records to CSV`);
      // Reset data after successful export
      setData([]);
    }
  }, [data, handlePrint, printInitiated, selectedFormat, setSuccess]);

  const resetPrintState = () => {
    setData([]);
    setPrintInitiated(false);
  };

  const downloadCSV = (csvData: unknown[], filename: string) => {
    if (!csvData || csvData.length === 0) {
      return;
    }

    // Generate CSV content
    const extractor = ScoreboardDataExtractor.getInstance(null as never);
    const csvContent = extractor.generateScoreboardCSV(csvData);

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    return () => {
      // This cleanup function runs when the component unmounts/closed
      resetPrintState();
    };
  }, []);

  useEffect(() => {
    if (!usersResult.fetching && !usersResult.error && usersResult.data?.company_users) {
      setCompanyUsers(usersResult.data.company_users);
    }
  }, [usersResult]);

  // Updated toggleEmailRecipients function
  const toggleEmailRecipients = (userOrUserId) => {
    if (typeof userOrUserId === 'object') {
      // Assuming userOrUserId is a user object being removed
      setEmailRecipients((currentRecipients) => currentRecipients.filter((user) => user.id !== userOrUserId.id));
      setCompanyUsers((currentUsers) => [...currentUsers, userOrUserId]);
    } else {
      // Assuming userOrUserId is a userId being added
      setShowRecipientsError(false);
      const userToAdd = companyUsers.find((user) => user.id === userOrUserId);
      if (userToAdd) {
        setEmailRecipients((currentRecipients) => [...currentRecipients, userToAdd]);
        setCompanyUsers((currentUsers) => currentUsers.filter((user) => user.id !== userOrUserId));
      }
    }
  };

  // Handler for changing the time range selection
  const handleTimeRangeChange = (event) => {
    const { value } = event.target;
    setShowTimeRangeError(false);
    if (value === 'Custom') {
      // Handle custom range logic here
      setSelectedTimeRange(value);
      // Optionally reset date pickers here
    } else {
      setSelectedTimeRange(value);
    }
  };

  // Handler for multiple selection of time ranges in scoreboard page
  const handleSectionSelectionScoreboard = (event) => {
    const { value } = event.target;
    const scoreboardSections = [...selectedScoreboardSections];
    setShowTimeRangeError(false);
    if (!selectedScoreboardSections.includes(value)) {
      setSelectedScoreboardSections([...scoreboardSections, value]);
    } else {
      const i = scoreboardSections.indexOf(value);
      scoreboardSections.splice(i, 1);
      setSelectedScoreboardSections(scoreboardSections);
    }
  };

  // Handler for changing the format selection
  const handleFormatSelection = (event) => {
    const { value } = event.target;
    setShowFormatError(false);
    setSelectedFormat(value);
  };

  const handleCustomUserEmailSubmit = (email) => {
    if (!isEmail(email)) {
      setshowEmailerror(true);
    } else {
      setshowEmailerror(false);
      const updatedCustomEmails = [...customEmailRecipients, email];
      setCustomemailRecipients(updatedCustomEmails);
      setCustomEmail('');
      setShowCustomEmailDialog(false);
    }
  };

  const handleCustomEmailRemoval = (recipient) => {
    const updatedCustomEmails = customEmailRecipients.filter((el) => el !== recipient);
    setCustomemailRecipients(updatedCustomEmails);
  };

  const handleExport = async () => {
    // Reset error states
    setShowTimeRangeError(false);
    setShowFormatError(false);
    setShowRecipientsError(false);

    // Validate selections
    if (!selectedTimeRange) {
      setShowTimeRangeError(true);
    }
    if (!selectedFormat) {
      setShowFormatError(true);
    }

    // Only require recipients for email exports
    if (exportMethod === ExportMethod.Email && emailRecipients.length === 0 && customEmailRecipients.length === 0) {
      setShowRecipientsError(true);
    }

    // Proceed with export if all selections are valid
    if (selectedTimeRange && selectedFormat) {
      if (exportMethod === ExportMethod.Email) {
        // Handle email export
        if (emailRecipients.length > 0 || customEmailRecipients.length > 0) {
          setIsEmailSending(true); // Email sending starts
          const response = await fetch(`/api/email/export-data`, {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
            },
            body: JSON.stringify({
              emailRecipients: [...emailRecipients.map((e) => e.user.email), ...customEmailRecipients],
              selectedFormat,
              selectedTimeRange,
              exportMethod,
              exportType: ExportTypes.Scoreboard,
              companyId,
              teamId,
              customTimeRange: {
                startDate: startDate?.toDate(),
                endDate: endDate?.toDate(),
              },
              sections: selectedScoreboardSections,
            }),
          });
          if (response.status == 200) {
            setIsEmailSending(false);
            setEmailSent(true);
            setTimeout(() => {
              setEmailSent(false);
            }, 1500);
            setSuccess('Export Successful.');
            setIsOpen(false);
          } else if (response.status == 204) {
            setIsEmailSending(false);
            setError({
              message: 'No records found for the specified duration',
              name: 'No Content',
            });
          } else {
            setIsEmailSending(false);
            setError({
              message: 'There was an error sending the Email.',
              name: 'Error',
            });
          }
        }
      } else if (exportMethod === ExportMethod.Print) {
        // Handle print/CSV export
        await fetchDataAndPrint();
      }
    }
  };

  return (
    <Fragment>
      <Transition.Root show={isOpen}>
        <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={setIsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl z-10 overflow-y-auto">
                  <div className="bg-white px-6 pb-6 pt-6 sm:p-8 sm:pb-6 max-h-[90vh] overflow-y-auto">
                    <div className="sm:flex sm:items-start ">
                      <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left font-Poppins w-full">
                        <Dialog.Title as="h3" className="text-xl leading-6 font-semibold text-gray-900 mb-6">
                          Export Content
                        </Dialog.Title>
                        <div className="flex flex-col space-y-6">
                          {/* Scoreboard Export Sections */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-sm font-medium text-gray-700 mb-3">Section</div>
                            <div className="grid grid-cols-1 gap-3">
                              <div className="flex items-center">
                                <input
                                  id="scoreboard-section-daily"
                                  type="checkbox"
                                  value={ScoreBoardExportSections.Daily}
                                  checked={selectedScoreboardSections.includes(ScoreBoardExportSections.Daily)}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                  onChange={handleSectionSelectionScoreboard}
                                />
                                <label htmlFor="scoreboard-section-daily" className="ml-3 text-sm text-gray-700">
                                  {ScoreBoardExportSections.Daily}
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  id="scoreboard-section-weekly"
                                  type="checkbox"
                                  value={ScoreBoardExportSections.Weekly}
                                  checked={selectedScoreboardSections.includes(ScoreBoardExportSections.Weekly)}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                  onChange={handleSectionSelectionScoreboard}
                                />
                                <label htmlFor="scoreboard-section-weekly" className="ml-3 text-sm text-gray-700">
                                  {ScoreBoardExportSections.Weekly}
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  id="scoreboard-section-monthly"
                                  type="checkbox"
                                  value={ScoreBoardExportSections.Monthly}
                                  checked={selectedScoreboardSections.includes(ScoreBoardExportSections.Monthly)}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                  onChange={handleSectionSelectionScoreboard}
                                />
                                <label htmlFor="scoreboard-section-monthly" className="ml-3 text-sm text-gray-700">
                                  {ScoreBoardExportSections.Monthly}
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  id="scoreboard-section-quarterly"
                                  type="checkbox"
                                  value={ScoreBoardExportSections.Quarterly}
                                  checked={selectedScoreboardSections.includes(ScoreBoardExportSections.Quarterly)}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                  onChange={handleSectionSelectionScoreboard}
                                />
                                <label htmlFor="scoreboard-section-quarterly" className="ml-3 text-sm text-gray-700">
                                  {ScoreBoardExportSections.Quarterly}
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  id="scoreboard-section-yearly"
                                  type="checkbox"
                                  value={ScoreBoardExportSections.Annually}
                                  checked={selectedScoreboardSections.includes(ScoreBoardExportSections.Annually)}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                  onChange={handleSectionSelectionScoreboard}
                                />
                                <label htmlFor="scoreboard-section-yearly" className="ml-3 text-sm text-gray-700">
                                  {ScoreBoardExportSections.Annually}
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Time Range Selection */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-sm font-medium text-gray-700 mb-3">Time Range</div>
                            <div className="space-y-3">
                              <div className="flex items-center">
                                <input
                                  id="checkbox-all-time"
                                  type="checkbox"
                                  value={ExportTimeRangeOptions.All}
                                  checked={selectedTimeRange === ExportTimeRangeOptions.All}
                                  onChange={handleTimeRangeChange}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <label htmlFor="checkbox-all-time" className="ml-3 text-sm text-gray-700">
                                  All Time
                                </label>
                              </div>
                              <div className="flex items-center">
                                <input
                                  id="checkbox-custom-range"
                                  type="checkbox"
                                  value="Custom"
                                  checked={selectedTimeRange === 'Custom'}
                                  onChange={handleTimeRangeChange}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <label htmlFor="checkbox-custom-range" className="ml-3 text-sm text-gray-700">
                                  Custom Range
                                </label>
                              </div>
                              {selectedTimeRange === 'Custom' && (
                                <div className="ml-7 mt-3 p-3 bg-white rounded-md border border-gray-200">
                                  <div className="flex flex-col space-y-3">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Select Date Range:
                                    </label>
                                    {/* Custom Date Input Fields */}
                                    <div className="flex items-center space-x-2">
                                      <input
                                        type="text"
                                        id="start-date-input"
                                        placeholder="08-12-2025"
                                        value={startDate ? startDate.format('MM-DD-YYYY') : ''}
                                        onClick={() => {
                                          setFocusedInput('startDate');
                                        }}
                                        readOnly
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer bg-white"
                                      />
                                      <span className="text-gray-500 text-sm font-medium">to</span>
                                      <input
                                        type="text"
                                        id="end-date-input"
                                        placeholder="08-24-2025"
                                        value={endDate ? endDate.format('MM-DD-YYYY') : ''}
                                        onClick={() => {
                                          setFocusedInput('endDate');
                                        }}
                                        readOnly
                                        className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer bg-white"
                                      />
                                    </div>

                                    {/* Debug info */}
                                    {focusedInput && (
                                      <div className="text-xs text-blue-600 mt-1">Focused: {focusedInput}</div>
                                    )}

                                    {/* DateRangePicker - Show calendar when focused */}
                                    {focusedInput && (
                                      <div className="mt-4 relative">
                                        <div className="h-0 mt-[-70px]">
                                          {/* <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm font-medium text-gray-700">Select Date Range</span>
                                            <button
                                              onClick={() => setFocusedInput(null)}
                                              className="text-gray-400 hover:text-gray-600"
                                            >
                                              ×
                                            </button>
                                          </div> */}
                                          <DateRangePicker
                                            startDateId="scoreboard-start-date"
                                            endDateId="scoreboard-end-date"
                                            startDate={startDate}
                                            endDate={endDate}
                                            onDatesChange={({ startDate: _startDate, endDate: _endDate }) => {
                                              setStartDate(_startDate);
                                              setEndDate(_endDate);
                                              // Close calendar when both dates are selected
                                              if (_startDate && _endDate) {
                                                setTimeout(() => setFocusedInput(null), 200);
                                              }
                                            }}
                                            focusedInput={focusedInput}
                                            onFocusChange={(newFocusedInput) => {
                                              setFocusedInput(newFocusedInput);
                                            }}
                                            numberOfMonths={1}
                                            isOutsideRange={() => false}
                                            displayFormat="MM/DD/YYYY"
                                            showClearDates={false}
                                            hideKeyboardShortcutsPanel
                                            small={false}
                                            keepOpenOnDateSelect={false}
                                            calendarInfoPosition="bottom"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            {showTimeRangeError && (
                              <p className="text-red-500 text-xs mt-2 ml-7">Please select a time range.</p>
                            )}
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-sm font-medium text-gray-700 mb-3">Format</div>
                            <div className="space-y-3">
                              <div className="flex items-center">
                                <input
                                  id="radio-csv"
                                  type="radio"
                                  name="format"
                                  value={ExportFormat.CSV}
                                  checked={selectedFormat === ExportFormat.CSV}
                                  onChange={handleFormatSelection}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                                />
                                <label htmlFor="radio-csv" className="ml-3 text-sm text-gray-700">
                                  CSV
                                </label>
                              </div>
                            </div>
                            {showFormatError && (
                              <p className="text-red-500 text-xs mt-2 ml-7">Please select a valid format.</p>
                            )}
                          </div>
                          <div className="my-3 w-full">
                            <div className="text-sm font-bold">Method</div>
                            <div className="flex flex-row mt-2 hover:cursor-pointer">
                              <div
                                onClick={() => setExportMethod(ExportMethod.Email)}
                                className={`flex flex-1 border p-2 rounded-md mr-1 cursor-pointer ${
                                  exportMethod === ExportMethod.Email
                                    ? 'bg-blue-100 border-blue-600'
                                    : 'border-gray-600'
                                }`}
                              >
                                <AtSymbolIcon className="w-6 h-6 mr-1" />
                                <div className="text-sm flex justify-center items-center">Email</div>
                              </div>
                              <div
                                onClick={() => setExportMethod(ExportMethod.Print)}
                                className={`flex flex-1 border p-2 rounded-md mr-1 cursor-pointer ${
                                  exportMethod === ExportMethod.Print
                                    ? 'bg-blue-100 border-blue-600'
                                    : 'border-gray-600'
                                }`}
                              >
                                <Download className="w-6 h-6 mr-1" />
                                <div className="text-sm flex justify-center items-center">Download</div>
                              </div>
                            </div>
                          </div>
                          {exportMethod === ExportMethod.Email && (
                            <>
                              <div className="-mt-6 w-full">
                                <Menu>
                                  <div className="mt-1">
                                    <Menu.Button className="w-full rounded-md px-4 py-2 h-9 text-left cursor-default text-xs inline-flex justify-between items-center border border-gray-300 shadow-sm bg-white font-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-primary-500 ">
                                      Select Team Members to Email
                                      <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
                                    </Menu.Button>
                                  </div>
                                  <div className="relative z-30 overflow-visible">
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform opacity-0 scale-95"
                                      enterTo="transform opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform opacity-100 scale-100"
                                      leaveTo="transform opacity-0 scale-95"
                                    >
                                      <Menu.Items
                                        static
                                        className="max-h-24 mt-1 text-xs w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none overflow-y-scroll items-center flex-col justify-center"
                                      >
                                        {companyUsers?.map((company_user) => (
                                          <Menu.Item key={company_user.id}>
                                            {({ active }) => (
                                              <div
                                                className={classNames(
                                                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                  'cursor-pointer select-none relative py-2 pl-3 pr-9 ',
                                                )}
                                                onClick={() => toggleEmailRecipients(company_user.id)}
                                              >
                                                <div className="flex items-center">
                                                  <Avatar size="m" user={company_user.user} showName={true} />
                                                </div>
                                              </div>
                                            )}
                                          </Menu.Item>
                                        ))}
                                      </Menu.Items>
                                    </Transition>
                                  </div>
                                </Menu>
                              </div>
                              <div className="w-full flex mt-4 flex-wrap -m-2 overflow-y-auto max-h-64">
                                {emailRecipients.map((recipient) => {
                                  return (
                                    <div
                                      key={recipient.id}
                                      className="m-1 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 text-sm"
                                    >
                                      <Avatar size="s" user={recipient.user} showName={true} />
                                      <div className="cursor-pointer" onClick={() => toggleEmailRecipients(recipient)}>
                                        <XIcon height="16" width="16" />
                                      </div>
                                    </div>
                                  );
                                })}
                                {customEmailRecipients.map((recipient) => {
                                  return (
                                    <div
                                      key={recipient}
                                      className="m-1 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 text-sm"
                                    >
                                      <div className="flex flex-shrink-0 flex-grow-0 items-center justify-center text-base">
                                        {recipient}
                                      </div>
                                      <div
                                        className="cursor-pointer"
                                        onClick={() => handleCustomEmailRemoval(recipient)}
                                      >
                                        <XIcon height="16" width="16" />
                                      </div>
                                    </div>
                                  );
                                })}

                                <button
                                  type="button"
                                  className="m-1 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 text-sm"
                                  onClick={() => {
                                    setShowCustomEmailDialog(true);
                                  }}
                                >
                                  <PlusIcon height="16" width="16" />
                                  <span className="ml-1">Add Email</span>
                                </button>
                              </div>
                              {showRecipientsError && (
                                <p className="text-red-500 text-xs mt-2">Please add valid email recipients.</p>
                              )}

                              {showCustomEmailDialog && (
                                <>
                                  <Transition appear show={showCustomEmailDialog} as={Fragment}>
                                    <Dialog
                                      as="div"
                                      className="relative z-10"
                                      onClose={() => setShowCustomEmailDialog(false)}
                                    >
                                      <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
                                      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                                      <Transition.Child
                                        as={Fragment}
                                        enter="ease-out duration-300"
                                        enterFrom="opacity-0"
                                        enterTo="opacity-100"
                                        leave="ease-in duration-200"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                      >
                                        <div className="fixed inset-0 bg-black/25" />
                                      </Transition.Child>

                                      <div className="fixed inset-0 overflow-y-auto">
                                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                                          <Transition.Child
                                            as={Fragment}
                                            enter="ease-out duration-300"
                                            enterFrom="opacity-0 scale-95"
                                            enterTo="opacity-100 scale-100"
                                            leave="ease-in duration-200"
                                            leaveFrom="opacity-100 scale-100"
                                            leaveTo="opacity-0 scale-95"
                                          >
                                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                                              <Dialog.Title
                                                as="h3"
                                                className="text-base leading-10 text-lg font-bold  text-gray-800"
                                              >
                                                Add Email Address
                                              </Dialog.Title>
                                              <label htmlFor="title" className="block text-xs text-gray-700">
                                                Add an email address outside of your team to export the content to.
                                              </label>

                                              <label htmlFor="title" className="block text-xs text-gray-700 mt-4">
                                                Email Address
                                              </label>
                                              <div className="mt-1">
                                                <input
                                                  type="text"
                                                  name="email"
                                                  id="email"
                                                  value={customEmail}
                                                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-xs border-gray-300 rounded-md"
                                                  placeholder={`Email Address`}
                                                  onChange={(event) => setCustomEmail(event.target.value)}
                                                />
                                                {showEmailError && (
                                                  <p className="text-red-500 text-xs mt-1">
                                                    Please use a valid email Address.
                                                  </p>
                                                )}
                                              </div>
                                              <div className=" sm:flex sm:flex-row-reverse ">
                                                <div className="mt-4">
                                                  <button
                                                    type="button"
                                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                    onClick={() => {
                                                      setshowEmailerror(false);
                                                      setCustomEmail('');
                                                      setShowCustomEmailDialog(false);
                                                    }}
                                                  >
                                                    Cancel
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className={classNames(
                                                      button.primary({ isDisabled: false }),
                                                      'ml-2 w-auto inline-flex items-center justify-center rounded-md text-sm',
                                                    )}
                                                    onClick={() => handleCustomUserEmailSubmit(customEmail)}
                                                  >
                                                    Add
                                                  </button>
                                                </div>
                                              </div>
                                            </Dialog.Panel>
                                          </Transition.Child>
                                        </div>
                                      </div>
                                    </Dialog>
                                  </Transition>
                                </>
                              )}
                            </>
                          )}
                        </div>
                        <div className="flex w-full invisible">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to export the content with the above settings? This action cannot be
                            undone.
                          </p>
                        </div>
                        {exportMethod === ExportMethod.Email && (
                          <Fragment>
                            <div className="w-full">
                              {/* <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-sm font-medium text-gray-700 mb-3">Email Recipients</div>
                                <Menu as="div">
                                  <div className="mt-1">
                                    <Menu.Button className="w-full rounded-md px-3 py-2 text-left cursor-pointer text-sm inline-flex justify-between items-center border border-gray-300 shadow-sm bg-white font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500 transition-colors">
                                      Select Team Members to Email
                                      <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
                                    </Menu.Button>
                                  </div>
                                  <div className="relative z-30 overflow-visible">
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform opacity-0 scale-95"
                                      enterTo="transform opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform opacity-100 scale-100"
                                      leaveTo="transform opacity-0 scale-95"
                                    >
                                      <Menu.Items
                                        static
                                        className="max-h-24 mt-1 text-xs w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none overflow-y-scroll items-center flex-col justify-center"
                                      >
                                        {companyUsers?.map((company_user) => (
                                          <Menu.Item key={company_user.id}>
                                            {({ active }) => (
                                              <div
                                                className={classNames(
                                                  active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                                  'cursor-pointer select-none relative py-2 pl-3 pr-9 ',
                                                )}
                                                onClick={() => toggleEmailRecipients(company_user.id)}
                                              >
                                                <div className="flex items-center">
                                                  <Avatar size="m" user={company_user.user} showName={true} />
                                                </div>
                                              </div>
                                            )}
                                          </Menu.Item>
                                        ))}
                                      </Menu.Items>
                                    </Transition>
                                  </div>
                                </Menu>
                              </div> */}
                              {/* <div className="w-full flex mt-4 flex-wrap -m-2 overflow-y-auto max-h-24">
                                {emailRecipients.map((recipient) => {
                                  return (
                                    <div
                                      key={recipient.id}
                                      className="m-1 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 text-sm"
                                    >
                                      <Avatar size="s" user={recipient.user} showName={true} />
                                      <div className="cursor-pointer" onClick={() => toggleEmailRecipients(recipient)}>
                                        <XIcon height="16" width="16" />
                                      </div>
                                    </div>
                                  );
                                })}
                                {customEmailRecipients.map((recipient) => {
                                  return (
                                    <div
                                      key={recipient}
                                      className="m-1 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 text-sm"
                                    >
                                      <div className="flex flex-shrink-0 flex-grow-0 items-center justify-center text-base">
                                        {recipient}
                                      </div>
                                      <div
                                        className="cursor-pointer"
                                        onClick={() => handleCustomEmailRemoval(recipient)}
                                      >
                                        <XIcon height="16" width="16" />
                                      </div>
                                    </div>
                                  );
                                })}

                                <button
                                  type="button"
                                  className="m-1 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm flex items-center space-x-2 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 text-sm"
                                  onClick={() => {
                                    setShowCustomEmailDialog(true);
                                  }}
                                >
                                  <PlusIcon height="16" width="16" />
                                  <span className="ml-1">Add Email</span>
                                </button>
                              </div> */}
                              {showRecipientsError && (
                                <p className="text-red-500 text-xs mt-2">Please add valid email recipients.</p>
                              )}
                            </div>

                            {showCustomEmailDialog || (
                              <Fragment>
                                <Transition appear show={showCustomEmailDialog} as={Fragment}>
                                  <Dialog
                                    as="div"
                                    className="relative z-10"
                                    onClose={() => setShowCustomEmailDialog(false)}
                                  >
                                    <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
                                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                                    <Transition.Child
                                      as={Fragment}
                                      enter="ease-out duration-300"
                                      enterFrom="opacity-0"
                                      enterTo="opacity-100"
                                      leave="ease-in duration-200"
                                      leaveFrom="opacity-100"
                                      leaveTo="opacity-0"
                                    >
                                      <div className="fixed inset-0 bg-black/25" />
                                    </Transition.Child>

                                    <div className="fixed inset-0 overflow-y-auto">
                                      <div className="flex min-h-full items-center justify-center p-4 text-center">
                                        <Transition.Child
                                          as={Fragment}
                                          enter="ease-out duration-300"
                                          enterFrom="opacity-0 scale-95"
                                          enterTo="opacity-100 scale-100"
                                          leave="ease-in duration-200"
                                          leaveFrom="opacity-100 scale-100"
                                          leaveTo="opacity-0 scale-95"
                                        >
                                          <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                                            <Dialog.Title
                                              as="h3"
                                              className="text-base leading-10 text-lg font-bold  text-gray-800"
                                            >
                                              Add Email Address
                                            </Dialog.Title>
                                            <label htmlFor="title" className="block text-xs text-gray-700">
                                              Add an email address outside of your team to export the content to.
                                            </label>

                                            <label htmlFor="title" className="block text-xs text-gray-700 mt-4">
                                              Email Address
                                            </label>
                                            <div className="mt-1">
                                              <input
                                                type="text"
                                                name="email"
                                                id="email"
                                                value={customEmail}
                                                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-xs border-gray-300 rounded-md"
                                                placeholder={`Email Address`}
                                                onChange={(event) => setCustomEmail(event.target.value)}
                                              />
                                              {showEmailError && (
                                                <p className="text-red-500 text-xs mt-1">
                                                  Please use a valid email Address.
                                                </p>
                                              )}
                                            </div>
                                            <div className=" sm:flex sm:flex-row-reverse ">
                                              <div className="mt-4">
                                                <button
                                                  type="button"
                                                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                  onClick={() => {
                                                    setshowEmailerror(false);
                                                    setCustomEmail('');
                                                    setShowCustomEmailDialog(false);
                                                  }}
                                                >
                                                  Cancel
                                                </button>
                                                <button
                                                  type="button"
                                                  className={classNames(
                                                    button.primary({ isDisabled: false }),
                                                    'ml-2 w-auto inline-flex items-center justify-center rounded-md text-sm',
                                                  )}
                                                  onClick={() => handleCustomUserEmailSubmit(customEmail)}
                                                >
                                                  Add
                                                </button>
                                              </div>
                                            </div>
                                          </Dialog.Panel>
                                        </Transition.Child>
                                      </div>
                                    </div>
                                  </Dialog>
                                </Transition>
                              </Fragment>
                            )}
                          </Fragment>
                        )}
                      </div>
                    </div>
                    {isEmailSending && (
                      <div className="fixed bottom-0 left-0 right-0 h-1 bg-blue-600 overflow-hidden">
                        <div className="h-full bg-blue-400 animate-pulse"></div>
                      </div>
                    )}
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    {exportMethod === ExportMethod.Email ? (
                      <button
                        type="button"
                        className={classNames(
                          emailSent ? button.primary({ isDisabled: false }) : button.primary({ isDisabled: false }),
                          'w-auto inline-flex items-center justify-center rounded-md text-sm ml-2',
                        )}
                        disabled={isEmailSending}
                        onClick={handleExport}
                      >
                        {isEmailSending ? (
                          <Fragment>
                            <span className="mr-2 font-semibold">Sending</span>
                            <Loader color="white" />
                          </Fragment>
                        ) : emailSent ? (
                          <Fragment>
                            <span className="font-semibold">Sent</span>
                            <CheckIcon strokeWidth={15} className="w-4 h-4 ml-2" />
                          </Fragment>
                        ) : (
                          <Fragment>
                            <span className="font-semibold">Export</span>
                          </Fragment>
                        )}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={classNames(
                          button.primary({ isDisabled: false }),
                          'w-auto inline-flex items-center justify-center rounded-md text-sm ml-2',
                        )}
                        disabled={preparingPrint}
                        onClick={fetchDataAndPrint}
                      >
                        {preparingPrint ? (
                          <Fragment>
                            <span className="mr-2 font-semibold">Preparing</span>
                            <Loader color="white" />
                          </Fragment>
                        ) : (
                          <Fragment>
                            <span className="font-semibold">Export</span>
                          </Fragment>
                        )}
                      </button>
                    )}

                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        setIsOpen(false);
                      }}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                  <ErrorToast error={error} setError={setError} />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Hidden Print Component for Scoreboard */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          <h1>Scoreboard Export</h1>
          {/* Add your scoreboard print layout here */}
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </div>
      </div>
    </Fragment>
  );
};

export default ExportPopup;
